'use client'

import Link from 'next/link'
import { createContext } from 'react'
import { LuLock } from 'react-icons/lu'

import ErrorToast from '@/core/components/error-toast'
import { buttonStyles, Button } from '@/core/components/form/button/button'

import StudioLoading from '@/features/studio-renew/components/studio-loading'
import { useRenewAuth } from '@/features/studio-renew/use-renew-auth'
import { openAuth } from '@/features/veluvit/services/account.actions'

import type { ReactNode } from 'react'
import type { StudioInitialSnapshot } from '@/features/studio-renew/services/studio-read.server'

const MemberContext = createContext('')

/**
 * 회원 전용 문 — 게스트·비로그인은 로그인 안내, 회원만 스튜디오를 연다(기획 결정 10).
 * 로그인은 이용자 앱의 로그인 창을 이 자리에서 띄운다 — 마이로 보내지 않는다(2026-10-13). 로그인하면 세션이 바뀌어
 * useRenewAuth 가 다시 확인하고 문이 열린다. 간편 로그인은 떠난 주소(이 화면)로 돌아온다(social-login.ts).
 */
export default function RenewGate({
  children,
  initial,
}: {
  children: ReactNode
  initial?: StudioInitialSnapshot['auth']
}) {
  const auth = useRenewAuth(initial)

  if (auth.state === 'loading') return <StudioLoading />
  if (auth.state === 'member')
    return (
      <MemberContext.Provider value={auth.name}>
        {children}
      </MemberContext.Provider>
    )

  return (
    <div className="grid min-h-full place-items-center px-6 py-20 text-center text-t1">
      <div className="max-w-sm">
        <span className="mx-auto grid size-12 place-items-center rounded-full bg-raised text-t2">
          <LuLock aria-hidden="true" className="size-5" />
        </span>
        {auth.state === 'disabled' && (
          <>
            <h1 className="mt-4 text-c18 font-bold">
              지금은 스튜디오를 이용할 수 없어요
            </h1>
            <p className="mt-1.5 text-c13 over10:text-c14 leading-[1.6] text-t3">
              제작 기능을 다시 열면 이용할 수 있어요.
            </p>
          </>
        )}
        {auth.state === 'guest' && (
          <>
            <h1 className="mt-4 text-c18 font-bold">로그인하고 만들어요</h1>
            <p className="mt-1.5 text-c13 over10:text-c14 leading-[1.6] text-t3">
              스튜디오는 로그인한 회원만 쓸 수 있어요. 만든 작품은 내 계정에
              저장돼요.
            </p>
            <Button
              type="button"
              onClick={() => openAuth('login')}
              className={buttonStyles({
                variant: 'primary',
                bold: true,
                className: 'mt-5 px-6',
              })}
              variant="bare"
            >
              로그인
            </Button>
          </>
        )}
        {auth.state === 'error' && (
          <>
            <ErrorToast message={auth.message} />
            <Button
              type="button"
              onClick={() => location.reload()}
              className="mt-5 inline-flex h-10 cursor-pointer items-center rounded-control bg-raised px-5 text-c14 font-semibold text-t1 hover:bg-hover"
              variant="bare"
            >
              다시 시도
            </Button>
          </>
        )}
        <Link
          href="/"
          className="mt-3 block text-c12 over10:text-c14 text-t3 no-underline hover:text-t1"
        >
          veluvit 으로 돌아가기
        </Link>
      </div>
    </div>
  )
}
